import {
  ApolloError,
  gql,
  ObservableQuery,
  useMutation,
  useQuery
} from "@apollo/client";
import { Button, LinearProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ErrorBox } from "components/error";
import DataGrid, {
  FilterRow,
  Paging,
  Scrolling,
  Selection
} from "devextreme-react/data-grid";
import DropDownBox from "devextreme-react/drop-down-box";
import {
  PublicApiPermission,
  RestrictedPublicApiRealm
} from "features/access-management";
import { isEqual, map, property, uniqBy } from "lodash";
import { useCallback, useRef, useState } from "react";
import { notNullOrUndefined,nameof } from "utils/typescript";
import "../../utils/custom-dx-theme/dx.material.custom-scheme.css";
import {
  GrantApiKeyAccess,
  GrantApiKeyAccessVariables
} from "./schema/GrantApiKeyAccess";
import { QueryAvailableSolutions } from "./schema/QueryAvailableSolutions";
import { QueryResolveAccess } from "./schema/QueryResolveAccess";
import {
  RevokeApiKeyAccess,
  RevokeApiKeyAccessVariables
} from "./schema/RevokeApiKeyAccess";

export const resolveAccessQuery = gql`
  query QueryResolveAccess($apiKey: String!) {
    publicApi {
      access {
        resolveAccess(apiKey: $apiKey) {
          key
        }
      }
    }
  }
`;

export const availableSolutionsQuery = gql`
  query QueryAvailableSolutions {
    solutions {
      allSolutions {
        name
        key
      }
    }
  }
`;

const grantApiKeyAccessMutation = gql`
  mutation GrantApiKeyAccess(
    $apiKey: String!
    $solutionPermissions: [SolutionPermissionInput]!
  ) {
    publicApi {
      access {
        grantApiKeyAccess(
          apiKey: $apiKey
          solutionPermissions: $solutionPermissions
        )
      }
    }
  }
`;

const revokeApiKeyAccessMutation = gql`
  mutation RevokeApiKeyAccess($apiKey: String!) {
    publicApi {
      access {
        revokeApiKeyAccess(apiKey: $apiKey)
      }
    }
  }
`;

const useStyles = makeStyles(theme => ({
  flex: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end"
  },
  button: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#fff"
    }
  },
  disabledButton: {
    backgroundColor: "#C7CFD4",
    color: "#68777B"
  },
  dropdwon: {
    "&.dx-texteditor.dx-editor-filled .dx-placeholder::before": {
      content: "none"
    }
  }
}));
const ownerLabel = { "aria-label": "Owner" };

export default function ApiKeyDetail({
  data: {
    data: { value: apiKeyValue }
  }
}: any) {
  const classes = useStyles();
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const dropDownBoxRef = useRef<DropDownBox>(null);
  const [isSaveSolutionsDisabled, setIsSaveSolutionsDisabled] = useState(true);

  const {
    data: apiKeySolutionsKeys,
    loading,
    error: resolveAccessError
  } = useQuery<QueryResolveAccess>(resolveAccessQuery, {
    variables: { apiKey: apiKeyValue },
    fetchPolicy: "no-cache"
  });

  const { data: allAvailableSolutions, error: availableSolutionsError } =
    useQuery<QueryAvailableSolutions>(availableSolutionsQuery);

  const resolveAccessKeys: string[] = map(
    apiKeySolutionsKeys?.publicApi.access.resolveAccess,
    property("key")
  );

  const [solutions, setSolutions] = useState<string[]>([]);
  const [refetchError, setRefetchError] = useState<ApolloError | null>(null);

  const [grantApiKeyAccess, { error: grantApiKeyAccessError }] = useMutation<
    GrantApiKeyAccess,
    GrantApiKeyAccessVariables
  >(grantApiKeyAccessMutation);

  const [revokeApiKeyAccess, { error: revokeApiKeyAccessError }] = useMutation<
    RevokeApiKeyAccess,
    RevokeApiKeyAccessVariables
  >(revokeApiKeyAccessMutation);

  const handleQueryUpdated = (
    observableQuery: ObservableQuery,
    setRefetchError: React.Dispatch<React.SetStateAction<ApolloError | null>>
  ) => {
    return observableQuery.refetch().catch(error => {
      setRefetchError(error);
    });
  };

  const handleAssignSolutions = () => {
    const solutionsKeys = map(solutions, keyValue => ({
      key: keyValue
    }));

    if (solutionsKeys.length > 0) {
      grantApiKeyAccess({
        variables: {
          apiKey: apiKeyValue,
          solutionPermissions: solutionsKeys
        },
        refetchQueries: [
          { query: resolveAccessQuery, variables: { apiKey: apiKeyValue } }
        ],
        onQueryUpdated: observableQuery =>
          handleQueryUpdated(observableQuery, setRefetchError)
      });
    } else {
      revokeApiKeyAccess({
        variables: { apiKey: apiKeyValue },
        refetchQueries: [
          { query: resolveAccessQuery, variables: { apiKey: apiKeyValue } }
        ],
        onQueryUpdated: observableQuery =>
          handleQueryUpdated(observableQuery, setRefetchError)
      });
    }
    dropDownBoxRef.current?.instance?.close();
  };

  const datascource = uniqBy(
    [
      ...(allAvailableSolutions?.solutions.allSolutions || []),
      ...(apiKeySolutionsKeys?.publicApi.access.resolveAccess || [])
    ],
    "key"
  );

  const handleContentReady = useCallback(() => {
    if (solutions.length === 0) {
      setSolutions(resolveAccessKeys);
    }
  }, [resolveAccessKeys, solutions.length]);

  const errors = [
    resolveAccessError,
    availableSolutionsError,
    grantApiKeyAccessError,
    revokeApiKeyAccessError,
    refetchError
  ].filter(notNullOrUndefined);

  if (loading) return <LinearProgress />;
  if (errors.length > 0) {
    return <ErrorBox apolloError={errors} />;
  }
  function dataGridRender() {
    return (
      <div>
        <div className={classes.flex}>
          <Button
            onMouseDown={handleAssignSolutions}
            ref={saveButtonRef}
            className={classes.button}
            disabled={isSaveSolutionsDisabled}
            classes={{
              root: classes.button,
              disabled: classes.disabledButton
            }}
          >
            Save
          </Button>
        </div>

        <DataGrid
          height={345}
          dataSource={datascource}
          columns={["key"]}
          keyExpr="key"
          hoverStateEnabled={true}
          selectedRowKeys={solutions}
          onSelectionChanged={e => {
            const selectedRowKeys = e.selectedRowKeys;
            const noChangeOccurs = isEqual(
              [...resolveAccessKeys].sort(),
              [...selectedRowKeys].sort()
            );
            setSolutions((e.selectedRowKeys.length && e.selectedRowKeys) || []);
            setIsSaveSolutionsDisabled(noChangeOccurs);
          }}
          onKeyDown={e => {
            if (e.event?.key === "Enter") {
              e.event.stopPropagation();
              saveButtonRef.current?.focus();
            }
          }}
          onContentReady={handleContentReady}
        >
          <Selection mode="multiple" />
          <Scrolling mode="virtual" />
          <Paging enabled={true} pageSize={10} />
          <FilterRow visible={true} />
        </DataGrid>
      </div>
    );
  }
  return (
    <RestrictedPublicApiRealm
      requiredPermissions={[PublicApiPermission.ChangeApiKeyAccess]}
    >
      {({ accessGranted }) => {
        return (
          <div className="dx-field">
            <div className="dx-field-label">
              Solutions that can use this API Key
            </div>
            <div className="dx-field-value">
              <DropDownBox
                value={solutions}
                valueExpr={nameof("key")}
                displayExpr={nameof("key")}
                deferRendering={false}
                inputAttr={ownerLabel}
                valueChangeEvent="valueChanged"
                placeholder="Select a value..."
                showClearButton={true}
                dataSource={datascource}
                onValueChanged={e => {
                  const keysValue = e.value;
                  setSolutions(keysValue || []);
                }}
                contentRender={dataGridRender}
                disabled={!accessGranted}
                ref={dropDownBoxRef}
                className={classes.dropdwon}
              />
            </div>
          </div>
        );
      }}
    </RestrictedPublicApiRealm>
  );
}
